// Thanks @fumadocs

'use client';

import { CheckIcon, CopyIcon, TriangleAlertIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useOptimistic, useTransition } from 'react';

import { motionIconProps } from '@/components/copy-button';

const cache = new Map<string, string>();

export function LLMCopyButton({ markdownUrl }: { markdownUrl: string }) {
  const [state, setState] = useOptimistic<'idle' | 'copied' | 'failed'>('idle');
  const [, startTransition] = useTransition();

  const handleCopy = () => {
    startTransition(async () => {
      try {
        setState('copied');

        const cached = cache.get(markdownUrl);
        if (cached) {
          await navigator.clipboard.writeText(cached);
          return;
        }

        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': fetch(markdownUrl)
              .then((res) => res.text())
              .then((content) => {
                cache.set(markdownUrl, content);
                return content;
              }),
          }),
        ]);
      } catch {
        setState('failed');
      } finally {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    });
  };

  return (
    <button
      type="button"
      className="flex h-7 items-center gap-1.5 rounded-l-full pr-2 pl-2.5 text-sm font-medium disabled:pointer-events-none disabled:opacity-50"
      onClick={handleCopy}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {state === 'idle' ? (
          <motion.span key="idle" {...motionIconProps}>
            <CopyIcon />
          </motion.span>
        ) : state === 'copied' ? (
          <motion.span key="copied" {...motionIconProps}>
            <CheckIcon strokeWidth={3} />
          </motion.span>
        ) : state === 'failed' ? (
          <motion.span key="failed" {...motionIconProps}>
            <TriangleAlertIcon />
          </motion.span>
        ) : null}
      </AnimatePresence>
      MDX
    </button>
  );
}
